import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../App';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getDayData, months } from '../data/curriculum';
import MultiCodeEditor from '../components/MultiCodeEditor';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
  ExternalLink,
  CheckCircle2,
  BookOpen,
  Code2,
  FileText,
  GitBranch,
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  ArrowLeft,
  Mail,
  X,
  HelpCircle,
  Lock,
  Circle,
  Github,
  Pencil,
  Send,
  AlertCircle,
} from 'lucide-react';

export default function DayDetailPage() {
  const { dayNumber } = useParams();
  const { token, API, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();

  const [dayData, setDayData] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [allProgress, setAllProgress] = useState([]);
  const [loading, setLoading] = useState({});
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [evalChecked, setEvalChecked] = useState({});
  const [gitSubmission, setGitSubmission] = useState(null);
  const [gitEditing, setGitEditing] = useState(false);
  const [gitRepo, setGitRepo] = useState('');
  const [gitBranch, setGitBranch] = useState('');
  const [gitLoading, setGitLoading] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const isResizingSidebar = useRef(false);
  const sidebarResizeStartX = useRef(0);
  const sidebarResizeStartWidth = useRef(240);

  // Scroll to top whenever the day changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [dayNumber]);

  // Reset eval checklist and git state when day changes
  useEffect(() => {
    setEvalChecked({});
    setGitSubmission(null);
    setGitEditing(false);
    setGitRepo('');
    setGitBranch('');
  }, [dayNumber]);

  // Auto-expand the current month + week in the sidebar
  useEffect(() => {
    const currentDay = parseInt(dayNumber, 10);
    const newMonths = new Set();
    const newWeeks = new Set();
    months.forEach((month) => {
      month.weeks.forEach((week) => {
        if (week.days.includes(currentDay)) {
          newMonths.add(month.id);
          newWeeks.add(week.id);
        }
      });
    });
    setExpandedMonths(newMonths);
    setExpandedWeeks(newWeeks);
  }, [dayNumber]);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllProgress(res.data);
      const dayProgress = res.data.find((p) => p.day_number === parseInt(dayNumber, 10));
      setCompletedTasks(dayProgress?.completed_tasks || []);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch progress', err);
      return null;
    }
  }, [API, token, dayNumber]);

  const fetchGitSubmission = useCallback(async () => {
    if (isAdmin) return;
    try {
      const res = await axios.get(`${API}/progress/git/${dayNumber}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data && res.data.repo_url) {
        setGitSubmission(res.data);
        setGitRepo(res.data.repo_url);
        setGitBranch(res.data.branch);
        setGitEditing(false);
      }
    } catch (err) {
      console.error('Failed to fetch git submission', err);
    }
  }, [API, token, dayNumber, isAdmin]);

  useEffect(() => {
    const dayNum = parseInt(dayNumber, 10);
    // Try PG curriculum first, then fall back to static + Mongo overrides
    axios.get(`${API}/pg-curriculum/days/${dayNum}?course_slug=aiml`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (res.data && res.data.day) {
        setDayData(res.data);
      } else {
        throw new Error('Empty PG response');
      }
    }).catch(() => {
      // Fall back to static data
      const staticData = getDayData(dayNum);
      setDayData(staticData);
      // Merge Mongo overrides for static fallback
      axios.get(`${API}/curriculum/${dayNumber}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((res) => {
        if (res.data && Object.keys(res.data).length > 0) {
          setDayData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ...(res.data.topic ? { topic: res.data.topic } : {}),
              ...(res.data.resource_links ? { resourceLinks: res.data.resource_links } : {}),
            };
          });
        }
      }).catch(() => {});
    });
    fetchProgress();
    fetchGitSubmission();
  }, [dayNumber, fetchProgress, fetchGitSubmission, API, token]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizingSidebar.current) return;
      const delta = e.clientX - sidebarResizeStartX.current;
      const newWidth = Math.min(400, Math.max(160, sidebarResizeStartWidth.current + delta));
      setSidebarWidth(newWidth);
    };
    const onMouseUp = () => {
      isResizingSidebar.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Display: shows ✓ in sidebar — only when backend has confirmed is_completed
  // Using is_completed as sole source of truth prevents adding new curriculum tasks
  // from visually reverting a day that was already completed.
  const isDayCompleted = (dayNum) => {
    const p = allProgress.find((pr) => pr.day_number === dayNum);
    return p?.is_completed === true;
  };

  // Access control: next day only unlocks after backend sets is_completed (git required)
  const isDayUnlocked = (dayNum) => {
    if (isAdmin || dayNum === 1) return true;
    const prev = allProgress.find((p) => p.day_number === dayNum - 1);
    return prev?.is_completed === true;
  };

  const attemptDayCompletion = async () => {
    try {
      await axios.post(
        `${API}/progress/complete-day`,
        { day_number: parseInt(dayNumber, 10) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Day ${dayNumber} completed! Next day is now unlocked.`);
      await fetchProgress();
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg === 'You must submit your Git work before completing the day') {
        toast.info('Please submit your Git work below to complete the day.');
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error(msg || 'Failed to complete the day. Please try again.');
      }
    }
  };

  const handleTaskToggle = async (taskId) => {
    const isCompleting = !completedTasks.includes(taskId);

    // Optimistic update — reflect change immediately in the UI
    const prevCompleted = completedTasks;
    const newCompleted = isCompleting
      ? [...completedTasks, taskId]
      : completedTasks.filter((t) => t !== taskId);
    setCompletedTasks(newCompleted);
    setLoading((prev) => ({ ...prev, [taskId]: true }));

    try {
      await axios.post(
        `${API}/progress/complete-task`,
        { day_number: parseInt(dayNumber, 10), task_id: taskId, completed: isCompleting },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // All tasks done — check if git is also submitted
      if (dayData && newCompleted.length === dayData.tasks.length) {
        if (isAdmin || gitSubmission) {
          await attemptDayCompletion();
        } else {
          toast.info('All tasks done! Submit your Git work below to complete the day.');
        }
      }
    } catch (err) {
      // Revert to previous state on failure
      setCompletedTasks(prevCompleted);
      const msg = err.response?.data?.detail;
      toast.error(msg || 'Failed to update task. Please try again.');
    } finally {
      setLoading((prev) => ({ ...prev, [taskId]: false }));
      fetchProgress();
    }
  };

  const handleGitSubmit = async () => {
    if (!token) { toast.error('Session expired. Please log in again.'); return; }
    if (!gitRepo.trim()) { toast.error('Please enter a GitHub repository URL'); return; }
    if (!gitRepo.trim().startsWith('https://github.com/')) { toast.error('URL must start with https://github.com/'); return; }
    if (!gitBranch.trim()) { toast.error('Please enter a branch name'); return; }

    setGitLoading(true);
    try {
      const res = await axios.post(
        `${API}/progress/submit-git`,
        { day_number: parseInt(dayNumber, 10), repo_url: gitRepo.trim(), branch: gitBranch.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGitSubmission(res.data);
      setGitEditing(false);
      toast.success('Git work submitted!');

      // Re-fetch progress; fall back to current local state if the fetch fails
      const latestProgress = await fetchProgress();
      const dayProg = latestProgress?.find((p) => p.day_number === parseInt(dayNumber, 10));
      const latestCompleted = dayProg?.completed_tasks ?? completedTasks;

      // If all tasks are also done, complete the day
      if (dayData && latestCompleted.length >= dayData.tasks.length) {
        await attemptDayCompletion();
      } else {
        toast.info('All tasks must be completed to finish the day.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Session expired. Please log in again.');
      } else if (!err.response) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(msg || 'Failed to submit Git work. Please try again.');
      }
    } finally {
      setGitLoading(false);
    }
  };

  const toggleMonth = (id) =>
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleWeek = (id) =>
    setExpandedWeeks((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleEval = (idx) =>
    setEvalChecked((prev) => ({ ...prev, [idx]: !prev[idx] }));

  if (!dayData) {
    return (
      <div className="mesh-bg min-h-screen flex items-center justify-center">
        <div className="glass-panel flex flex-col items-center gap-4 px-6 py-8 rounded-xl">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-300 dark:border-slate-700 border-t-cyan-500" />
          <p className="font-heading text-lg text-slate-600 dark:text-slate-300">Loading day plan...</p>
        </div>
      </div>
    );
  }

  const currentDay = parseInt(dayNumber, 10);
  const completionPct = Math.round((completedTasks.length / dayData.tasks.length) * 100);
  // Strict check — Next button and completion badge require git to be submitted
  const isDayTrulyCompleted = allProgress.some(
    (p) => p.day_number === currentDay && p.is_completed === true
  );
  const canNavigateToPrev = currentDay > 1;
  const canNavigateToNext = currentDay < 120 && (isAdmin || isDayTrulyCompleted);

  const supportContacts = [
    { name: 'Krishna Kompalli', email: 'krishna.kompalli@flyerssoft.com' },
    { name: 'Keerthi Ramakrishna', email: 'keerthi.ramakrishna@flyerssoft.com' },
    { name: 'Shalini P', email: 'shalini.p@flyerssoft.com' },
  ];

  const panelClass =
    'glass-panel p-6 rounded-2xl border border-slate-300 dark:border-white/5 bg-white dark:bg-slate-900/40 backdrop-blur-sm';

  return (
    <div className="min-h-screen pb-12">

      {/* ── Sticky top bar ── */}
      <div className="sticky top-0 z-40 border-b border-slate-200 dark:border-white/5 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-3 sm:h-[72px]">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <Link to="/dashboard">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 px-2.5 sm:px-3 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Button>
              </Link>

              <Separator orientation="vertical" className="hidden h-6 sm:block bg-slate-200 dark:bg-white/10" />

              <div className="min-w-0">
                <Badge className="mb-1 bg-cyan-100 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-400 border-cyan-200 dark:border-cyan-900/50 hover:bg-cyan-200 dark:hover:bg-cyan-900/50">
                  Day {dayNumber}
                </Badge>
                <p className="truncate text-xs text-slate-500">
                  Month {dayData.month} | Week {dayData.week}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHelpModal(true)}
                className="gap-1.5 px-2.5 sm:px-3 border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
              >
                <HelpCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Need Help</span>
              </Button>

              <Separator orientation="vertical" className="h-6 bg-slate-200 dark:bg-white/10" />

              <Button
                variant="ghost"
                size="icon"
                disabled={!canNavigateToPrev}
                onClick={() => navigate(`/dashboard/day/${currentDay - 1}`)}
                className="text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                disabled={!canNavigateToNext}
                onClick={() => navigate(`/dashboard/day/${currentDay + 1}`)}
                className="text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Help modal ── */}
      {showHelpModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShowHelpModal(false)}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-slate-50 dark:bg-gradient-to-r dark:from-cyan-950 dark:to-slate-900 p-6 border-b border-slate-200 dark:border-white/5">
              <div className="absolute top-0 right-0 p-4">
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-200 dark:hover:bg-white/10 hover:text-slate-700 dark:hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-600 dark:text-cyan-400">
                  <HelpCircle className="h-5 w-5" />
                </div>
                <h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white">Priority Support</h2>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 pl-1">
                Stuck on a task? Our mentors are ready to help you unblock.
              </p>
            </div>

            {/* Contact cards */}
            <div className="p-6 space-y-3 bg-white dark:bg-slate-950/50">
              {supportContacts.map((contact) => (
                <a
                  key={contact.email}
                  href={`mailto:${contact.email}`}
                  className="group flex items-center gap-4 rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-900 p-4 transition-all hover:border-cyan-500/30 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white font-bold text-xs">
                    {contact.name.charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 dark:text-white group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                      {contact.name}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {contact.email}
                    </p>
                  </div>
                  <Mail className="h-4 w-4 text-slate-400 group-hover:text-cyan-500 dark:group-hover:text-cyan-400 transition-colors" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Main body: sidebar + content ── */}
      <div className="mx-auto max-w-[1400px] flex">

        {/* ── Left sidebar curriculum navigation ── */}
        <aside
          className="hidden lg:block shrink-0 bg-slate-50 dark:bg-transparent"
          style={{ width: sidebarWidth }}
        >
          <div className="sticky top-[65px] sm:top-[73px] max-h-[calc(100vh-73px)] overflow-y-auto">
            <div className="p-3 pt-4">
              <p className="mb-3 px-2 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">
                Curriculum
              </p>

              {months.map((month) => {
                const isMonthExpanded = expandedMonths.has(month.id);
                return (
                  <div key={month.id} className="mb-0.5">
                    {/* Month row */}
                    <button
                      onClick={() => toggleMonth(month.id)}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-slate-200 dark:hover:bg-white/5"
                    >
                      <ChevronRight
                        className={`h-3.5 w-3.5 shrink-0 text-slate-500 dark:text-slate-400 transition-transform duration-200 ${isMonthExpanded ? 'rotate-90' : ''}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                          M{month.id}: {month.title}
                        </p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{month.subtitle}</p>
                      </div>
                    </button>

                    {/* Weeks */}
                    {isMonthExpanded && (
                      <div className="ml-3 border-l border-slate-300 dark:border-white/5 pl-2 mt-0.5 mb-1 space-y-0.5">
                        {month.weeks.map((week) => {
                          const isWeekExpanded = expandedWeeks.has(week.id);
                          return (
                            <div key={week.id}>
                              {/* Week row */}
                              <button
                                onClick={() => toggleWeek(week.id)}
                                className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-slate-200 dark:hover:bg-white/5"
                              >
                                <ChevronRight
                                  className={`h-3 w-3 shrink-0 text-slate-500 dark:text-slate-400 transition-transform duration-200 ${isWeekExpanded ? 'rotate-90' : ''}`}
                                />
                                <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 truncate">
                                  {week.title}
                                </span>
                              </button>

                              {/* Days */}
                              {isWeekExpanded && (
                                <div className="ml-3 mt-0.5 mb-1 space-y-0.5">
                                  {week.days.map((day) => {
                                    const completed = isDayCompleted(day);
                                    const unlocked = isDayUnlocked(day);
                                    const isActive = day === currentDay;
                                    return (
                                      <button
                                        key={day}
                                        disabled={!unlocked}
                                        onClick={() => unlocked && navigate(`/dashboard/day/${day}`)}
                                        className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] transition-all
                                          ${isActive
                                            ? 'bg-cyan-500/15 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 font-semibold'
                                            : unlocked
                                              ? 'text-slate-700 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white'
                                              : 'text-slate-600 dark:text-slate-500 cursor-not-allowed bg-slate-100 dark:bg-transparent'
                                          }`}
                                      >
                                        {completed ? (
                                          <CheckCircle2 className="h-3 w-3 shrink-0 text-green-500" />
                                        ) : unlocked ? (
                                          <Circle className="h-3 w-3 shrink-0 text-slate-500 dark:text-slate-500" />
                                        ) : (
                                          <Lock className="h-3 w-3 shrink-0 text-slate-500 dark:text-slate-500" />
                                        )}
                                        Day {day}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        {/* ── Sidebar resize handle ── */}
        <div
          className="hidden lg:block w-1 shrink-0 cursor-col-resize border-r border-slate-300 dark:border-white/5 hover:border-cyan-400 dark:hover:border-cyan-500 transition-colors"
          onMouseDown={(e) => {
            isResizingSidebar.current = true;
            sidebarResizeStartX.current = e.clientX;
            sidebarResizeStartWidth.current = sidebarWidth;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            e.preventDefault();
          }}
        />

        {/* ── Day content ── */}
        <main className="flex-1 min-w-0 px-4 py-8 sm:px-6 lg:px-8">

          {/* Hero section — no "Learning Sprint" label */}
          <section className="glass-panel relative mb-8 overflow-hidden p-7 rounded-2xl bg-white dark:bg-white/5 border border-slate-300 dark:border-white/10">
            <div className="absolute -right-16 -top-12 h-64 w-64 rounded-full bg-cyan-500/10 dark:bg-cyan-500/20 blur-3xl" />

            <div className="relative">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge className="bg-cyan-100 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-400 border-cyan-200 dark:border-cyan-900/50">
                  Day {dayNumber}
                </Badge>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {dayData.monthTitle} · {dayData.weekTitle}
                </span>
              </div>

              <h1 className="font-heading text-3xl font-bold leading-tight text-slate-900 dark:text-white sm:text-4xl">
                {dayData.topic}
              </h1>

              <div className="mt-6 flex items-center gap-3">
                <Progress
                  value={completionPct}
                  className="h-2 flex-1 bg-slate-200 dark:bg-slate-800"
                  indicatorClassName="bg-cyan-500"
                />
                <span className="min-w-[52px] text-right text-sm font-bold text-slate-700 dark:text-slate-300">
                  {completionPct}%
                </span>
                {isDayTrulyCompleted && <CheckCircle2 className="h-6 w-6 shrink-0 text-green-500" />}
              </div>
            </div>
          </section>

          <div className="space-y-6">

            {/* Lesson Overview */}
            {dayData.content && (
              <section className={panelClass}>
                <div className="mb-4 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                  <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Lesson Overview</h2>
                </div>

                {dayData.overview && (
                  <p className="mb-6 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                    {dayData.overview}
                  </p>
                )}

                <div className="space-y-6">
                  {dayData.content.map((section, i) => (
                    <div key={i}>
                      <h3 className="mb-2 text-sm font-bold text-slate-800 dark:text-slate-200">
                        {section.heading}
                      </h3>
                      {section.intro && (
                        <p className="mb-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                          {section.intro}
                        </p>
                      )}
                      {section.points && section.points.length > 0 && (
                        <ul className="space-y-2">
                          {section.points.map((pt, j) => (
                            <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-400">
                              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500" />
                              <span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{pt.bold} </span>
                                {pt.text}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Tasks */}
            <section className={panelClass}>
              <div className="mb-4 flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Tasks</h2>
                <span className="ml-auto text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                  {completedTasks.length}/{dayData.tasks.length}
                </span>
              </div>

              <div className="space-y-2.5">
                {dayData.tasks.map((task) => {
                  const checked = completedTasks.includes(task.id);
                  return (
                    <label
                      key={task.id}
                      className={`flex items-start gap-3 rounded-xl border px-3.5 py-3 transition-all ${isAdmin ? 'cursor-default' : 'cursor-pointer'} ${checked
                        ? 'border-green-500/20 bg-green-500/10'
                        : 'border-slate-300 dark:border-white/5 bg-slate-100 dark:bg-white/5 hover:border-slate-400 dark:hover:border-white/10 hover:bg-slate-200 dark:hover:bg-white/10'
                        }`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={isAdmin || loading[task.id]}
                        onCheckedChange={() => !isAdmin && handleTaskToggle(task.id)}
                        className="mt-0.5 border-slate-400 dark:border-slate-600 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                      />
                      <span className={`text-sm leading-relaxed ${checked ? 'text-slate-500 line-through' : 'text-slate-700 dark:text-slate-200'}`}>
                        {task.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>

            {/* Git Submission — interns only */}
            {!isAdmin && (
              <section className={panelClass}>
                <div className="mb-4 flex items-center gap-2">
                  <Github className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                  <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Git Submission</h2>
                  {gitSubmission && !gitEditing
                    ? <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-green-600 dark:text-green-400"><CheckCircle2 className="h-4 w-4" />Submitted</span>
                    : <span className="ml-auto text-xs font-semibold uppercase tracking-wider text-red-500">Required</span>
                  }
                </div>

                {gitSubmission && !gitEditing ? (
                  // Submitted — read-only view
                  <div className="rounded-xl border border-green-500/20 bg-green-500/10 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Repository</p>
                        <a href={gitSubmission.repo_url} target="_blank" rel="noopener noreferrer"
                          className="text-sm font-medium text-cyan-600 dark:text-cyan-400 hover:underline break-all">
                          {gitSubmission.repo_url}
                        </a>
                      </div>
                      <Button variant="outline" size="sm"
                        onClick={() => setGitEditing(true)}
                        className="shrink-0 gap-1.5 border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/10">
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </Button>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Branch</p>
                      <p className="font-mono text-sm text-slate-700 dark:text-slate-300">{gitSubmission.branch}</p>
                    </div>
                  </div>
                ) : (
                  // Form — new submission or editing
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Push your work to GitHub and submit the repo URL + branch to complete this day.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          GitHub Repository URL
                        </label>
                        <input
                          type="url"
                          placeholder="https://github.com/your-username/your-repo"
                          value={gitRepo}
                          onChange={(e) => setGitRepo(e.target.value)}
                          disabled={gitLoading}
                          className="w-full rounded-xl border border-slate-300 dark:border-white/10 bg-slate-50 dark:bg-slate-900 px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 disabled:opacity-50"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Branch Name
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. main, day-1, feature/day1-work"
                          value={gitBranch}
                          onChange={(e) => setGitBranch(e.target.value)}
                          disabled={gitLoading}
                          className="w-full rounded-xl border border-slate-300 dark:border-white/10 bg-slate-50 dark:bg-slate-900 px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 disabled:opacity-50"
                        />
                      </div>
                    </div>

                    {!gitRepo.trim().startsWith('https://github.com/') && gitRepo.trim().length > 0 && (
                      <div className="flex items-center gap-2 rounded-xl border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        URL must start with https://github.com/
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        onClick={handleGitSubmit}
                        disabled={gitLoading || !gitRepo.trim() || !gitBranch.trim()}
                        className="gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-500 hover:to-blue-500 disabled:opacity-40"
                      >
                        {gitLoading
                          ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          : <Send className="h-4 w-4" />}
                        {gitLoading ? 'Submitting…' : 'Submit Git Work'}
                      </Button>
                      {gitEditing && (
                        <Button variant="outline" onClick={() => {
                          setGitEditing(false);
                          setGitRepo(gitSubmission?.repo_url || '');
                          setGitBranch(gitSubmission?.branch || '');
                        }}
                          className="border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300">
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Learning Resources */}
            {dayData.resourceLinks && dayData.resourceLinks.length > 0 && (
              <section className={panelClass}>
                <div className="mb-4 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                  <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Learning Resources</h2>
                </div>

                <div className="space-y-4">
                  {dayData.resourceLinks.map((link, idx) => {
                    const getYouTubeId = (url) => {
                      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                      const match = url.match(regExp);
                      return match && match[2].length === 11 ? match[2] : null;
                    };

                    const videoId = getYouTubeId(link.url);
                    const isYouTube = videoId !== null;

                    if (isYouTube) {
                      return (
                        <div key={idx} className="space-y-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{link.title}</span>
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-600 dark:text-cyan-400 hover:text-cyan-500"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Open in YouTube
                            </a>
                          </div>
                          <div
                            className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-white/10 shadow-lg"
                            style={{ paddingBottom: '56.25%' }}
                          >
                            <iframe
                              className="absolute left-0 top-0 h-full w-full"
                              src={`https://www.youtube.com/embed/${videoId}`}
                              title={link.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                            />
                          </div>
                        </div>
                      );
                    }

                    return (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-white/5 p-4 hover:bg-white dark:hover:bg-white/10 transition-colors"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                          <ExternalLink className="h-5 w-5" />
                        </div>
                        <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                          {link.title}
                        </span>
                        <ExternalLink className="h-4 w-4 text-slate-400 dark:text-slate-600 group-hover:text-cyan-600 dark:group-hover:text-white transition-colors" />
                      </a>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Coding Task */}
            <section className={panelClass}>
              <div className="mb-3 flex items-center gap-2">
                <Code2 className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Coding Task</h2>
              </div>
              <p className="rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-950/50 p-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-mono">
                {dayData.codingTask}
              </p>
            </section>

            {/* In-Browser IDE */}
            <section className={panelClass}>
              <div className="mb-4 flex items-center gap-2">
                <Code2 className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Try It Here</h2>
                <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">Powered by Wandbox</span>
              </div>
              <MultiCodeEditor dayNumber={dayNumber} />
            </section>

            {/* Assignment */}
            <section className={panelClass}>
              <div className="mb-3 flex items-center gap-2">
                <FileText className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Assignment</h2>
              </div>
              <p className="rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-950/50 p-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {dayData.assignment}
              </p>
            </section>

            {/* Hands-on Practice */}
            {dayData.handsOn && dayData.handsOn.length > 0 && (
              <section className={panelClass}>
                <div className="mb-3 flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                  <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Hands-on Practice</h2>
                </div>
                <ul className="space-y-2 rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-950/50 p-4">
                  {dayData.handsOn.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-cyan-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Why This Matters */}
            {dayData.explanation && (
              <section className="glass-panel overflow-hidden border border-cyan-200 dark:border-cyan-500/20 bg-gradient-to-br from-cyan-50 dark:from-cyan-950/30 to-slate-50 dark:to-slate-900/50 p-6 rounded-2xl">
                <h2 className="font-heading text-xl font-semibold text-cyan-700 dark:text-cyan-400">Why This Matters</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-300">{dayData.explanation}</p>
              </section>
            )}

            {/* Evaluation Checklist — interactive tickboxes */}
            {dayData.evaluationChecklist && dayData.evaluationChecklist.length > 0 && (
              <section className={panelClass}>
                <div className="mb-4 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                  <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Evaluation Checklist</h2>
                  <span className="ml-auto text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                    {Object.values(evalChecked).filter(Boolean).length}/{dayData.evaluationChecklist.length}
                  </span>
                </div>

                <div className="space-y-2.5">
                  {dayData.evaluationChecklist.map((item, idx) => {
                    const checked = !!evalChecked[idx];
                    return (
                      <label
                        key={idx}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 transition-all ${checked
                          ? 'border-green-500/20 bg-green-500/10'
                          : 'border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-950/50 hover:border-slate-300 dark:hover:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5'
                          }`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleEval(idx)}
                          className="mt-0.5 border-slate-400 dark:border-slate-600 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                        />
                        <span className={`text-sm leading-relaxed ${checked ? 'text-slate-500 line-through' : 'text-slate-700 dark:text-slate-300'}`}>
                          {item}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Git Task */}
            {dayData.gitTask && (
              <section className={panelClass}>
                <div className="mb-3 flex items-center gap-2">
                  <GitBranch className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                  <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Git Task</h2>
                </div>
                <p className="font-mono rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-950/50 p-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                  {dayData.gitTask}
                </p>
              </section>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}
